import React, { useEffect, useState } from "react";
import CartBannerHeader from "./CartBannerHeader";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import API_ENDPOINTS, { IMAGE_BASE_URL } from "../../api/apiConfig";
import '../../assets/css/theme.css';
import '../../assets/css/cart.css';
import type { Order } from "../../types";

const FinalOrderSummary = () => {
  const [order, setOrder]     = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string>("");
  const navigate              = useNavigate();

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("authToken");
      if (!token) {
        setError("You must be logged in to view your order.");
        setLoading(false);
        return;
      }
      try {
        const res  = await fetch(API_ENDPOINTS.GET_USER_ORDER, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (res.ok && data.status && Array.isArray(data.orders) && data.orders.length > 0) {
          const latestOrder = data.orders.reduce((prev: Order, curr: Order) =>
            prev.order_id > curr.order_id ? prev : curr
          );
          setOrder(latestOrder);
        } else {
          setError(data.message || "No orders found.");
        }
      } catch {
        setError("Failed to fetch order.");
      }
      setLoading(false);
    };
    fetchOrder();
  }, []);

  // ✅ Coupon helpers
  const hasCoupon = order && order.coupon_code;
  const subtotalAmount = hasCoupon
    ? Number(order.total_amount) + Number(order.discount_amount || 0)
    : Number(order?.total_amount || 0);

  return (
    <main className="ffc-page">
      <CartBannerHeader currentStep={4} />

      <section className="ffc-body">
        <div className="ffc-container">
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="ffc-empty">
              <span className="ffc-empty-icon" aria-hidden="true">
                <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" />
                </svg>
              </span>
              <h2 className="ffc-empty-title" style={{ fontSize: 30 }}>{error}</h2>
              <button type="button" className="ffh-btn" onClick={() => navigate("/")}>Back to home</button>
            </div>
          ) : order ? (
            <>
              {/* Success hero */}
              <div className="ffc-success">
                <div className="ffc-success-ring" aria-hidden="true">
                  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <h2 className="ffc-success-title">Order <em>confirmed</em></h2>
                <p className="ffc-success-sub">Thank you for your purchase. We'll keep you updated.</p>

                <div className="ffc-order-meta">
                  <span className="ffc-order-id">Order<b>#{order.order_id}</b></span>
                  <span className="ffc-status">{order.status}</span>
                </div>
              </div>

              <div className="ffc-layout">
                {/* ── Left: shipping + items ── */}
                <div>
                  <div className="ffc-card">
                    <h3 className="ffc-card-title">Shipping information</h3>
                    <div className="ffc-info-grid">
                      <div>
                        <div className="ffc-info-label">Full name</div>
                        <div className="ffc-info-value">{order.address.full_name}</div>
                      </div>
                      <div>
                        <div className="ffc-info-label">Email</div>
                        <div className="ffc-info-value">{order.address.email}</div>
                      </div>
                      <div>
                        <div className="ffc-info-label">Phone</div>
                        <div className="ffc-info-value">{order.address.contact_no}</div>
                      </div>
                      <div>
                        <div className="ffc-info-label">Address type</div>
                        <div className="ffc-info-value" style={{ textTransform: "capitalize" }}>
                          {order.address.address_type}
                        </div>
                      </div>
                      <div className="ffc-info-item--wide">
                        <div className="ffc-info-label">Delivery address</div>
                        <div className="ffc-info-value">
                          {order.address.address_line}, {order.address.city},{" "}
                          {order.address.state} — {order.address.postal_code}
                        </div>
                      </div>
                      <div>
                        <div className="ffc-info-label">Payment method</div>
                        <div className="ffc-info-value">{order.payment_method}</div>
                      </div>
                    </div>
                  </div>

                  <div className="ffc-card">
                    <h3 className="ffc-card-title">Items ordered</h3>
                    {order.items.map((item, idx) => (
                      <div
                        key={item.product_id + "-" + item.size + "-" + idx}
                        className="ffc-line"
                      >
                        <img
                          src={
                            item.product_image
                              ? item.product_image.startsWith("http")
                                ? item.product_image
                                : `${IMAGE_BASE_URL}/media/${item.product_image}`
                              : "/placeholder.jpg"
                          }
                          alt={item.product_name}
                          className="ffc-line-img"
                        />
                        <div className="ffc-line-info">
                          <div className="ffc-line-name">{item.product_name}</div>
                          <div className="ffc-line-meta">
                            Size: {item.size} &nbsp;·&nbsp; Qty: {item.quantity}
                          </div>
                          <div className="ffc-line-price">
                            {item.discount_percentage && item.discount_percentage > 0 ? (
                              <>
                                <s>₹{item.product_price.toFixed(2)}</s>
                                ₹{item.price.toFixed(2)}
                              </>
                            ) : (
                              <>₹{item.price.toFixed(2)}</>
                            )}
                          </div>
                        </div>
                        <div className="ffc-line-total">₹{item.total_price.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Right: coupon + price breakdown + CTA ── */}
                <aside className="ffc-side">
                  {hasCoupon && (
                    <div className="ffc-card ffc-coupon-summary">
                      <span className="ffc-coupon-summary-icon">✓</span>
                      <div>
                        <div>
                          Coupon <strong>{order.coupon_code}</strong> applied
                        </div>
                        <div className="ffc-coupon-summary-detail">
                          You saved ₹{Number(order.discount_amount).toFixed(2)} ({Number(order.coupon_discount_rate).toFixed(0)}% off)
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="ffc-card">
                    <h3 className="ffc-card-title">Price breakdown</h3>
                    <div className="ffc-row">
                      <span>Subtotal</span>
                      <span>₹{subtotalAmount.toFixed(2)}</span>
                    </div>
                    <div className="ffc-row">
                      <span>Shipping</span>
                      <span className="ffc-free">FREE</span>
                    </div>
                    {hasCoupon && (
                      <div className="ffc-row ffc-row--discount">
                        <span>Coupon discount ({Number(order.coupon_discount_rate).toFixed(0)}%)</span>
                        <span>− ₹{Number(order.discount_amount).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="ffc-divider" />
                    <div className="ffc-row ffc-row--total">
                      <span>Total</span>
                      <span>₹{Number(order.total_amount).toFixed(2)}</span>
                    </div>

                    <button
                      type="button"
                      className="ffh-btn ffc-btn--block"
                      style={{ marginTop: 24 }}
                      onClick={() => navigate("/")}
                    >
                      Continue shopping
                    </button>
                  </div>
                </aside>
              </div>
            </>
          ) : null}
        </div>
      </section>
    </main>
  );
};

export default FinalOrderSummary;